import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, Trash2, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const SUGGESTIONS = ["How does provably fair work?", "How do I deposit on mainnet?", "What's my VIP tier and rakeback?", "Explain Mega Slots paylines"];

const sessionId = () => {
  let s = localStorage.getItem("fmi_chat_session");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("fmi_chat_session", s);
  }
  return s;
};

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith("**")) return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith("`")) return <code key={i} className="font-mono-num text-[12px] px-1 rounded bg-black/30">{part.slice(1, -1)}</code>;
    return part;
  });

function Bubble({ m }) {
  const me = m.role === "user";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${me ? "bg-amber-500 text-black rounded-br-sm" : "bg-white/[0.06] text-zinc-100 rounded-bl-sm"}`}>
        {m.content ? renderInline(m.content) : <span className="inline-flex gap-1"><i className="chat-dot" /><i className="chat-dot" style={{ animationDelay: "150ms" }} /><i className="chat-dot" style={{ animationDelay: "300ms" }} /></span>}
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    sid.current = sessionId();
    setMsgs([]);
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("fmi_token");
    fetch(`${API}/api/assistant/history?session_id=${sid.current}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => (r.ok ? r.json() : [])).then((rows) => setMsgs(rows.map((r) => ({ role: r.role, content: r.content })))).catch(() => {});
  }, [open, user?.id]);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const token = localStorage.getItem("fmi_token");
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sid.current, message: q }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setMsgs((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.delta }; return n; });
          if (ev.error) setMsgs((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: ev.error }; return n; });
        }
      }
    } catch {
      setMsgs((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: "I couldn't reach the concierge. Please try again." }; return n; });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    const token = localStorage.getItem("fmi_token");
    await fetch(`${API}/api/assistant/history?session_id=${sid.current}`, { method: "DELETE", headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => {});
    setMsgs([]);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="assistant-fab bottom-24 md:bottom-6" data-testid="assistant-open" aria-label="Open concierge">
          <Bot size={22} />
          <span className="assistant-fab-label">Concierge</span>
        </button>
      )}
      {open && (
        <div className="assistant-panel" data-testid="assistant-panel">
          <div className="flex items-center gap-3 px-4 h-14 border-b border-white/[0.06]">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/15 text-amber-400"><Sparkles size={16} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm tracking-tight">FMI Concierge</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{user ? `Signed in as ${user.username}` : "GPT-powered · ask anything"}</div>
            </div>
            <button onClick={clear} className="btn-ghost rounded-lg p-2 text-zinc-500" title="Clear chat" data-testid="assistant-clear"><Trash2 size={14} /></button>
            <button onClick={() => setOpen(false)} className="btn-ghost rounded-lg p-2 text-zinc-500" data-testid="assistant-close"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 sidebar-scroll" data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="flex flex-col gap-3 fade-up">
                <div className="text-sm text-zinc-400">Hi{user ? ` ${user.username}` : ""} — I know every game, bonus and rule in the vault. What can I help with?</div>
                <div className="flex flex-col gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-left text-xs px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300" data-testid="assistant-suggestion" style={{ transition: "background-color 140ms ease" }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={bottom} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-white/[0.06]">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the concierge…" maxLength={2000}
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3.5 h-10 text-sm outline-none focus:border-amber-500/50" data-testid="assistant-input" />
            <button type="submit" disabled={busy || !input.trim()} className="btn-gold rounded-xl w-10 h-10 flex items-center justify-center disabled:opacity-40" data-testid="assistant-send"><Send size={15} /></button>
          </form>
        </div>
      )}
    </>
  );
}
